/**
 * Error Link 설정
 *
 * GraphQL 에러 처리 및 토큰 자동 갱신
 */

import { Observable } from "@apollo/client";
import { onError } from "@apollo/client/link/error";

import { STORAGE_KEYS } from '../storage';
import { classifyAuthError, handleAuthError } from '../errorUtils';
import {
  refreshAccessToken,
  getIsRefreshing,
  setIsRefreshing,
  addPendingRequest,
  resolvePendingRequests
} from '../tokenRefresh';

/**
 * ErrorLink - 인증 에러 시 토큰 갱신 후 자동 재시도
 */
export const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  // GraphQL 에러 로깅
  if (graphQLErrors) {
    console.group(`[ErrorLink] GraphQL Errors - ${operation.operationName || 'unknown'}`);
    graphQLErrors.forEach((err, index) => {
      console.error(`  Error ${index + 1}:`, {
        message: err.message,
        path: err.path,
        extensions: err.extensions,
      });
    });
    console.groupEnd();

    for (const err of graphQLErrors) {
      // 인증 에러 감지
      if (
        err.message === 'Authentication required' ||
        err.message === 'Token expired' ||
        err.extensions?.code === 'UNAUTHENTICATED'
      ) {
        console.log(`[ErrorLink] 인증 에러 감지 - 토큰 갱신 시도`);

        // 이미 토큰 갱신 중이면 대기열에 추가
        if (getIsRefreshing()) {
          return new Observable(observer => {
            addPendingRequest((token) => {
              if (token) {
                operation.setContext(({ headers = {} }) => ({
                  headers: {
                    ...headers,
                    authorization: `Bearer ${token}`,
                  },
                }));
                forward(operation).subscribe(observer);
              } else {
                observer.error(err);
              }
            });
          });
        }

        setIsRefreshing(true);

        return new Observable(observer => {
          refreshAccessToken()
            .then(result => {
              if (result.success && result.token) {
                resolvePendingRequests(result.token);

                operation.setContext(({ headers = {} }) => ({
                  headers: {
                    ...headers,
                    authorization: `Bearer ${result.token}`,
                  },
                }));

                forward(operation).subscribe({
                  next: observer.next.bind(observer),
                  error: observer.error.bind(observer),
                  complete: observer.complete.bind(observer),
                });
              } else {
                resolvePendingRequests(null);
                const errorType = result.errorType || 'UNKNOWN';

                if (errorType === 'NETWORK_ERROR') {
                  console.log('[ErrorLink] 네트워크 에러 - 토큰 유지');
                  window.dispatchEvent(new CustomEvent('network-error', {
                    detail: { source: 'ErrorLink', timestamp: Date.now() }
                  }));
                  observer.error(err);
                } else {
                  handleAuthError('ErrorLink', errorType);
                  observer.error(err);
                }
              }
            })
            .catch(error => {
              resolvePendingRequests(null);
              const errorType = classifyAuthError(error);
              console.error('[ErrorLink] 토큰 갱신 중 에러:', { error, errorType });

              if (errorType === 'NETWORK_ERROR') {
                window.dispatchEvent(new CustomEvent('network-error', {
                  detail: { source: 'ErrorLink', timestamp: Date.now() }
                }));
              } else {
                handleAuthError('ErrorLink', errorType);
              }
              observer.error(err);
            })
            .finally(() => {
              setIsRefreshing(false);
            });
        });
      }
    }
  }

  if (networkError) {
    const errorType = classifyAuthError(networkError);
    console.error('[ErrorLink] Network Error:', {
      message: networkError.message,
      statusCode: networkError.statusCode,
      operationName: operation.operationName,
      classifiedType: errorType
    });

    if (errorType === 'NETWORK_ERROR') {
      window.dispatchEvent(new CustomEvent('network-error', {
        detail: {
          source: 'NetworkError',
          operationName: operation.operationName,
          timestamp: Date.now()
        }
      }));
    }
  }
});
